import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilderStore } from '../../../stores/builderStore';
import type { LiveQuizSettingsConfig } from '../../../types/form';
import { generateUUID } from '../../../utils/uuid';
import MobileQuestionEditor from './MobileQuestionEditor';
import MobileQuizSettings from './MobileQuizSettings';
import MobileLanguageSelector from '../MobileLanguageSelector';
import MobileErrorBoundary from '../MobileErrorBoundary';

interface MobileLiveQuizBuilderProps {
  formId?: string;
  formName?: string;
  onSave?: () => Promise<void>;
  onBack?: () => void;
}

interface QuizQuestion {
  id: string;
  question: string;
  options: { id: string; text: string; isCorrect: boolean }[];
  timeLimit: number;
  points: number;
  imageUrl?: string;
}

const DEFAULT_LIVE_SETTINGS: LiveQuizSettingsConfig = {
  questionTimeSeconds: 20,
  showQuestionOnPlayerDevice: true,
  enableStreakBonus: true,
  streakBonusMultiplier: 1.2,
  streakThreshold: 3,
  timeBasedScoring: true,
  maxPointsPerQuestion: 1000,
  minPointsPerQuestion: 500,
  showLeaderboardAfterQuestion: true,
  leaderboardSize: 5,
  showAnswerDistribution: true,
  showCorrectAnswer: true,
  allowLateJoin: true,
  autoAdvance: false,
  autoAdvanceDelaySeconds: 5,
  randomizeQuestions: false,
  randomizeOptions: false,
  enableSounds: true,
};

type ActiveView = 'list' | 'editor' | 'settings';

export default function MobileLiveQuizBuilder({ formId: _formId, formName, onSave, onBack }: MobileLiveQuizBuilderProps) {
  const { t } = useTranslation('mobile');
  const { formSchema, updateSettings, updateQuizQuestions } = useBuilderStore();
  // formId reserved for future use (e.g., auto-save)
  void _formId;

  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ActiveView>('list');
  const [liveSettings, setLiveSettings] = useState<LiveQuizSettingsConfig>(
    formSchema.settings.quiz?.liveSettings || DEFAULT_LIVE_SETTINGS
  );
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showSaveError, setShowSaveError] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  // Touch handling for swipe
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipingQuestionId, setSwipingQuestionId] = useState<string | null>(null);

  // Ref for save guard to avoid dependency issues
  const isSavingRef = useRef(false);
  const saveSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveSuccessTimeoutRef.current) {
        clearTimeout(saveSuccessTimeoutRef.current);
      }
      if (saveErrorTimeoutRef.current) {
        clearTimeout(saveErrorTimeoutRef.current);
      }
    };
  }, []);

  // Convert form fields to quiz questions on initial load only
  useEffect(() => {
    if (isInitialized) return;

    const quizQuestions: QuizQuestion[] = formSchema.fields
      .filter(f => f.type === 'radio' || f.type === 'checkbox' || f.type === 'image_choice')
      .map(f => {
        const correctAnswer = f.properties?.correctAnswer;
        return {
          id: f.id,
          question: f.label,
          options: (f.options || []).map(opt => ({
            id: opt.value,
            text: opt.label,
            isCorrect: Array.isArray(correctAnswer)
              ? correctAnswer.includes(opt.value)
              : correctAnswer === opt.value,
          })),
          timeLimit: liveSettings.questionTimeSeconds,
          points: f.properties?.points || liveSettings.maxPointsPerQuestion,
          imageUrl: undefined,
        };
      });

    if (quizQuestions.length > 0) {
      setQuestions(quizQuestions);
    }
    setIsInitialized(true);
  }, [formSchema.fields, isInitialized, liveSettings.questionTimeSeconds, liveSettings.maxPointsPerQuestion]);

  // Sync settings changes to the store
  // Use a ref to track if we need to update to avoid infinite loops
  const prevLiveSettingsRef = useRef(liveSettings);
  useEffect(() => {
    // Only update if liveSettings actually changed
    if (prevLiveSettingsRef.current === liveSettings) return;
    prevLiveSettingsRef.current = liveSettings;

    const { formSchema: currentSchema } = useBuilderStore.getState();
    const quizSettings = currentSchema.settings.quiz || { enabled: true, mode: 'live' as const };
    updateSettings({
      quiz: {
        ...quizSettings,
        enabled: true,
        mode: 'live',
        liveSettings,
      },
    });
  }, [liveSettings, updateSettings]);

  // Sync questions to form fields with debouncing to prevent render cascades
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const questionsRef = useRef(questions);

  // Keep questionsRef updated
  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);

  // Cleanup sync timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  const syncQuestionsToFields = useCallback(() => {
    // Use ref to get latest questions, avoiding stale closure
    const currentQuestions = questionsRef.current;

    // Update fields in the store using the proper action
    // This ensures undo/redo works correctly
    updateQuizQuestions(currentQuestions);
  }, [updateQuizQuestions]);

  // Debounced sync effect
  useEffect(() => {
    if (questions.length > 0) {
      // Clear any pending sync
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      // Debounce the sync to batch rapid updates
      syncTimeoutRef.current = setTimeout(() => {
        syncQuestionsToFields();
      }, 150);
    }
  }, [questions, syncQuestionsToFields]);

  const addQuestion = useCallback(() => {
    const newQuestion: QuizQuestion = {
      id: generateUUID(),
      question: '',
      options: [
        { id: generateUUID(), text: '', isCorrect: true },
        { id: generateUUID(), text: '', isCorrect: false },
        { id: generateUUID(), text: '', isCorrect: false },
        { id: generateUUID(), text: '', isCorrect: false },
      ],
      timeLimit: liveSettings.questionTimeSeconds,
      points: liveSettings.maxPointsPerQuestion,
    };
    setQuestions(prev => [...prev, newQuestion]);
    setSelectedQuestionId(newQuestion.id);
    setActiveView('editor');
  }, [liveSettings.questionTimeSeconds, liveSettings.maxPointsPerQuestion]);

  const updateQuestion = useCallback((questionId: string, updates: Partial<QuizQuestion>) => {
    setQuestions(prev => prev.map(q =>
      q.id === questionId ? { ...q, ...updates } : q
    ));
  }, []);

  const deleteQuestion = useCallback((questionId: string) => {
    setQuestions(prev => {
      const newQuestions = prev.filter(q => q.id !== questionId);
      if (selectedQuestionId === questionId) {
        setSelectedQuestionId(newQuestions[0]?.id || null);
      }
      return newQuestions;
    });
    setSwipingQuestionId(null);
    setSwipeOffset(0);
  }, [selectedQuestionId]);

  const moveQuestion = useCallback((questionId: string, direction: 'up' | 'down') => {
    setQuestions(prev => {
      const index = prev.findIndex(q => q.id === questionId);
      if (index === -1) return prev;
      if (direction === 'up' && index === 0) return prev;
      if (direction === 'down' && index === prev.length - 1) return prev;

      const newQuestions = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
      return newQuestions;
    });
  }, []);

  // Touch handlers for swipe to delete
  const handleTouchStart = useCallback((questionId: string, e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    setSwipingQuestionId(questionId);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!swipingQuestionId) return;
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchStartX.current - touchCurrentX.current;
    if (diff > 0) {
      setSwipeOffset(Math.min(diff, 100));
    }
  }, [swipingQuestionId]);

  const handleTouchEnd = useCallback(() => {
    if (swipeOffset > 80 && swipingQuestionId) {
      deleteQuestion(swipingQuestionId);
    } else {
      setSwipeOffset(0);
      setSwipingQuestionId(null);
    }
  }, [swipeOffset, swipingQuestionId, deleteQuestion]);

  const handleSave = useCallback(async () => {
    if (!onSave || isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await onSave();
      setShowSaveSuccess(true);
      saveSuccessTimeoutRef.current = setTimeout(() => setShowSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      setShowSaveError(true);
      saveErrorTimeoutRef.current = setTimeout(() => setShowSaveError(false), 3000);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [onSave]);

  const selectedQuestion = questions.find(q => q.id === selectedQuestionId);

  // Validation
  const validationErrors: string[] = [];
  if (questions.length === 0) {
    validationErrors.push(t('liveQuiz.validation.addQuestion'));
  }
  questions.forEach((q, idx) => {
    if (!q.question.trim()) {
      validationErrors.push(t('liveQuiz.validation.missingText', { number: idx + 1 }));
    }
    if (q.options.filter(o => o.isCorrect).length === 0) {
      validationErrors.push(t('liveQuiz.validation.noCorrectAnswer', { number: idx + 1 }));
    }
    if (q.options.some(o => !o.text.trim())) {
      validationErrors.push(t('liveQuiz.validation.emptyOptions', { number: idx + 1 }));
    }
  });

  // Question Editor View
  if (activeView === 'editor' && selectedQuestion && selectedQuestionId) {
    return (
      <MobileErrorBoundary>
        <MobileQuestionEditor
          question={selectedQuestion}
          questionNumber={questions.findIndex(q => q.id === selectedQuestionId) + 1}
          onChange={(updates) => updateQuestion(selectedQuestionId, updates)}
          onBack={() => setActiveView('list')}
          onDelete={() => {
            deleteQuestion(selectedQuestionId);
            setActiveView('list');
          }}
        />
      </MobileErrorBoundary>
    );
  }

  // Settings View
  if (activeView === 'settings') {
    return (
      <MobileErrorBoundary>
        <MobileQuizSettings
          settings={liveSettings}
          onChange={setLiveSettings}
          onBack={() => setActiveView('list')}
        />
      </MobileErrorBoundary>
    );
  }

  // Question List View
  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-orange-500 via-orange-600 to-red-600">
      {/* Header */}
      <header className="px-4 py-3 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                aria-label="Go back"
                className="p-2 -ml-2 text-white/80 hover:text-white rounded-lg touch-manipulation"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div>
              <h1 className="font-bold text-white text-lg">
                {formName || t('liveQuiz.title')}
              </h1>
              <p className="text-white/70 text-sm">
                {t('liveQuiz.questionCount', { count: questions.length })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveView('settings')}
              aria-label="Quiz settings"
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg touch-manipulation"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              onClick={() => setShowMenu(true)}
              aria-label={t('common.openMenu')}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg touch-manipulation"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Questions List */}
      <div className="flex-1 overflow-y-auto px-4 pb-32">
        {questions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">{t('liveQuiz.noQuestionsTitle')}</h2>
            <p className="text-white/70 mb-6 max-w-xs">
              {t('liveQuiz.noQuestionsDescription')}
            </p>
            <button
              onClick={addQuestion}
              className="flex items-center gap-2 px-6 py-3 bg-white text-orange-600 rounded-xl font-bold shadow-lg active:scale-95 transition-transform touch-manipulation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('liveQuiz.addFirstQuestion')}
            </button>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            {questions.map((question, index) => {
              const correctCount = question.options.filter(o => o.isCorrect).length;
              const hasErrors = !question.question.trim() || correctCount === 0 || question.options.some(o => !o.text.trim());
              const isBeingSwiped = swipingQuestionId === question.id;

              return (
                <div
                  key={question.id}
                  className="relative overflow-hidden rounded-2xl"
                  onTouchStart={(e) => handleTouchStart(question.id, e)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {/* Delete background */}
                  <div className="absolute inset-y-0 right-0 w-24 bg-red-500 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </div>

                  {/* Question card */}
                  <div
                    className={`bg-white dark:bg-gray-800 rounded-2xl p-4 transform transition-transform touch-manipulation ${
                      hasErrors ? 'border-2 border-red-400' : ''
                    }`}
                    style={{
                      transform: isBeingSwiped ? `translateX(-${swipeOffset}px)` : 'translateX(0)',
                    }}
                    onClick={() => {
                      if (!isBeingSwiped || swipeOffset < 20) {
                        setSelectedQuestionId(question.id);
                        setActiveView('editor');
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Question Number */}
                      <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-orange-600 dark:text-orange-400">
                          {index + 1}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white line-clamp-2">
                          {question.question || t('liveQuiz.untitledQuestion')}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-sm">
                          <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {question.timeLimit}s
                          </span>
                          <span className="flex items-center gap-1 text-orange-500">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                            {question.points}
                          </span>
                          <span className={`${correctCount > 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {t('liveQuiz.options', { count: question.options.length })}
                          </span>
                        </div>
                      </div>

                      {/* Move and delete buttons */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveQuestion(question.id, 'up');
                          }}
                          disabled={index === 0}
                          aria-label={`Move question ${index + 1} up`}
                          className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded touch-manipulation"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveQuestion(question.id, 'down');
                          }}
                          disabled={index === questions.length - 1}
                          aria-label={`Move question ${index + 1} down`}
                          className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded touch-manipulation"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteQuestion(question.id);
                          }}
                          aria-label={`Delete question ${index + 1}`}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded touch-manipulation"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add button in list */}
            <button
              onClick={addQuestion}
              className="w-full py-4 border-2 border-dashed border-white/40 rounded-2xl text-white flex items-center justify-center gap-2 active:bg-white/10 transition-colors touch-manipulation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('liveQuiz.addQuestion')}
            </button>
          </div>
        )}
      </div>

      {/* Validation Errors - Fixed position above action bar */}
      {validationErrors.length > 0 && questions.length > 0 && (
        <div className="fixed bottom-24 left-4 right-4 bg-red-500/90 backdrop-blur-sm rounded-xl p-3 z-40">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-white flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-white">{t('liveQuiz.fixBeforePublishing')}</p>
              <p className="text-xs text-white/80 mt-1">
                {validationErrors.slice(0, 2).join(' • ')}
                {validationErrors.length > 2 && ` ${t('liveQuiz.moreErrors', { count: validationErrors.length - 2 })}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Action Bar - Fixed to bottom of viewport */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 px-4 py-3 safe-area-inset-bottom shadow-2xl z-40">
        <div className="flex items-center gap-3">
          <button
            onClick={addQuestion}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-orange-500 text-white rounded-xl font-bold shadow-lg shadow-orange-500/30 active:scale-[0.98] transition-transform touch-manipulation"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('liveQuiz.addQuestion')}
          </button>

          {onSave && (
            <button
              onClick={handleSave}
              disabled={isSaving || validationErrors.length > 0}
              className={`px-6 py-3 rounded-xl font-bold active:scale-[0.98] transition-all touch-manipulation ${
                validationErrors.length === 0
                  ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
              }`}
            >
              {isSaving ? (
                <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : showSaveSuccess ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                t('common.save')
              )}
            </button>
          )}
        </div>
      </div>

      {/* Save Success Toast */}
      {showSaveSuccess && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-slideDown">
          <div className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full shadow-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{t('builder.saved')}</span>
          </div>
        </div>
      )}

      {/* Save Error Toast */}
      {showSaveError && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-slideDown">
          <div className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full shadow-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="font-medium">{t('builder.saveFailed')}</span>
          </div>
        </div>
      )}

      {/* Menu Modal */}
      {showMenu && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl animate-slideUp safe-area-inset-bottom">
            <div className="flex justify-center py-3">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            <div className="px-4 pb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('builder.formOptions')}</h2>
              <div className="space-y-2">
                {/* Language Selector */}
                <MobileLanguageSelector onClose={() => setShowMenu(false)} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
